import type { Athlete } from '@/types'
import { preUsaAthlete } from './preUsaProgram'

/**
 * Sample roster used to preview the athlete experience before the Supabase data
 * layer (Phase 1) is wired. These programs are written in the new *structured*
 * format (load / tempo / rest / notes per exercise) to show the specificity the
 * old hardcoded `[name, sets, note]` tuples couldn't carry.
 */
export const SAMPLE_ATHLETES: Athlete[] = [
  preUsaAthlete,
  {
    id: '1',
    name: 'Marcus Reid',
    meta: 'Week 4 · Power Phase',
    currentWeek: 4,
    program: {
      id: 'p1',
      name: 'Vertical Power — Block 2',
      description: 'Max strength → rate of force development for jump performance.',
      goal_tags: ['power', 'vertical', 'basketball'],
      source: 'ai',
      weeks: [
        {
          week: 4,
          phase: 'POWER PHASE',
          note: 'Intent is everything this week — move every rep with maximum speed. Quality over fatigue.',
          days: [
            {
              label: 'Day 1',
              focus: 'Lower — Max Power',
              exercises: [
                { exercise_id: 'warmup', name: 'Warm-up', reps: '8–10 min', notes: 'Bike, then dynamic hips + ankles', is_warmup: true },
                { exercise_id: 'box-jump', name: 'Box Jumps', sets: 5, reps: '3', rest: '2 min', notes: 'Step down every rep. Reset for a max effort.' },
                { exercise_id: 'trap-bar-dl', name: 'Trap Bar Deadlift', sets: 4, reps: '3', load: '85% 1RM', tempo: 'X-0-1', rest: '3 min', notes: 'Explode off the floor.' },
                { exercise_id: 'bulgarian-split', name: 'Rear-Foot Elevated Split Squat', sets: 3, reps: '6 each leg', load: 'RPE 8', rest: '90s' },
                { exercise_id: 'depth-drop', name: 'Depth Drops', sets: 3, reps: '5', notes: 'From 30cm box — stick the landing, minimal ground time.' },
                { exercise_id: 'seated-calf', name: 'Seated Calf Raise', sets: 3, reps: '12', tempo: '2-1-2', rest: '60s' },
              ],
            },
            {
              label: 'Day 2',
              focus: 'Upper + Trunk',
              exercises: [
                { exercise_id: 'warmup', name: 'Warm-up', reps: '6–8 min', is_warmup: true },
                { exercise_id: 'bench', name: 'Bench Press', sets: 4, reps: '5', load: '80% 1RM', rest: '2–3 min' },
                { exercise_id: 'weighted-pullup', name: 'Weighted Pull-ups', sets: 4, reps: '5', load: '+10kg', rest: '2 min' },
                { exercise_id: 'db-push-press', name: 'DB Push Press', sets: 3, reps: '6', rest: '90s' },
                { exercise_id: 'pallof', name: 'Pallof Press', sets: 3, reps: '10 each side', notes: 'Resist rotation — slow and controlled.' },
                { exercise_id: 'hanging-leg-raise', name: 'Hanging Leg Raises', sets: 3, reps: '12' },
              ],
            },
            {
              label: 'Day 3',
              focus: 'Speed + Plyo',
              exercises: [
                { exercise_id: 'warmup', name: 'Warm-up', reps: '10 min', notes: 'Sprint drills + A-skips', is_warmup: true },
                { exercise_id: 'sprint', name: 'Sprints', sets: 6, reps: '20m', rest: 'Full recovery', notes: 'Max intent. Walk back between.' },
                { exercise_id: 'hurdle-hops', name: 'Hurdle Hops', sets: 4, reps: '5', notes: 'Stiff ankles, minimal contact time.' },
                { exercise_id: 'front-squat', name: 'Front Squat', sets: 4, reps: '4', load: '75% 1RM', tempo: 'X-0-2', rest: '2–3 min' },
                { exercise_id: 'nordic', name: 'Nordic Curls', sets: 3, reps: '6', notes: 'Slow eccentric — protect the hamstrings.' },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: '2',
    name: 'Ella Nguyen',
    meta: 'Week 2 · Return to Play',
    currentWeek: 2,
    program: {
      id: 'p2',
      name: 'ACL Return to Play — Phase 3',
      description: 'Progressive loading post-ACL. No deep knee flexion under load yet; monitor swelling.',
      goal_tags: ['rehab', 'acl', 'return-to-play'],
      source: 'ai',
      weeks: [
        {
          week: 2,
          phase: 'REBUILD PHASE',
          note: 'Symmetry first. Match left and right — do not chase load. Stop if knee swells or pain > 3/10.',
          days: [
            {
              label: 'Day 1',
              focus: 'Controlled Strength',
              exercises: [
                { exercise_id: 'warmup', name: 'Bike + Mobility', reps: '10 min', is_warmup: true },
                { exercise_id: 'leg-press', name: 'Leg Press (limited ROM)', sets: 4, reps: '8', load: 'RPE 6', tempo: '3-1-1', rest: '90s', notes: 'Stop at 60° knee flexion. Even push both legs.' },
                { exercise_id: 'ham-curl', name: 'Seated Hamstring Curl', sets: 3, reps: '10', tempo: '2-0-3', rest: '75s' },
                { exercise_id: 'step-up', name: 'Low Step-ups', sets: 3, reps: '8 each leg', notes: 'Control the descent — no knee cave.' },
                { exercise_id: 'calf-raise', name: 'Standing Calf Raise', sets: 3, reps: '15', rest: '45s' },
                { exercise_id: 'balance', name: 'Single-leg Balance', sets: 3, reps: '30s each', notes: 'Eyes closed if stable.' },
              ],
            },
            {
              label: 'Day 2',
              focus: 'Posterior + Trunk',
              exercises: [
                { exercise_id: 'warmup', name: 'Warm-up', reps: '8 min', is_warmup: true },
                { exercise_id: 'rdl', name: 'Romanian Deadlift', sets: 4, reps: '8', load: 'RPE 6', tempo: '3-0-1', rest: '90s' },
                { exercise_id: 'glute-bridge', name: 'Single-leg Glute Bridge', sets: 3, reps: '10 each', rest: '60s' },
                { exercise_id: 'copenhagen', name: 'Copenhagen Plank', sets: 3, reps: '20s each side' },
                { exercise_id: 'deadbug', name: 'Dead Bug', sets: 3, reps: '8 each side' },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: '3',
    name: 'Jordan Blake',
    meta: 'Week 7 · Strength Phase',
    currentWeek: 7,
    program: {
      id: 'p3',
      name: 'Base Strength — Block 3',
      goal_tags: ['strength'],
      source: 'manual',
      weeks: [
        {
          week: 7,
          phase: 'STRENGTH PHASE',
          note: null,
          days: [
            {
              label: 'Day 1',
              focus: 'Squat Focus',
              exercises: [
                { exercise_id: 'warmup', name: 'Warm-up', reps: '8 min', is_warmup: true },
                { exercise_id: 'back-squat', name: 'Back Squat', sets: 5, reps: '5', load: '82.5% 1RM', rest: '3 min' },
                { exercise_id: 'rdl', name: 'Romanian Deadlift', sets: 4, reps: '6', load: 'RPE 8', rest: '2 min' },
                { exercise_id: 'walking-lunge', name: 'Walking Lunges', sets: 3, reps: '10 each leg', rest: '90s' },
                { exercise_id: 'calf-raise', name: 'Standing Calf Raise', sets: 4, reps: '10', rest: '60s' },
              ],
            },
          ],
        },
      ],
    },
  },
  {
    id: '4',
    name: 'Sofia Marchetti',
    meta: 'Week 1 · Foundations',
    currentWeek: 1,
    program: {
      id: 'p4',
      name: 'Foundations',
      goal_tags: ['foundations', 'beginner'],
      source: 'manual',
      weeks: [
        {
          week: 1,
          phase: 'FOUNDATIONS PHASE',
          note: 'Learn the movements. Light loads, perfect technique.',
          days: [
            {
              label: 'Day 1',
              focus: 'Full Body',
              exercises: [
                { exercise_id: 'warmup', name: 'Warm-up', reps: '8 min', is_warmup: true },
                { exercise_id: 'goblet-squat', name: 'Goblet Squat', sets: 3, reps: '10', load: 'Light', tempo: '3-1-1', rest: '75s', notes: 'Go down slowly.' },
                { exercise_id: 'db-rdl', name: 'DB Romanian Deadlift', sets: 3, reps: '10', load: 'Light', rest: '75s' },
                { exercise_id: 'pushup', name: 'Push-ups', sets: 3, reps: '8–12', notes: 'Knees down if needed.' },
                { exercise_id: 'plank', name: 'Plank', sets: 3, reps: '20–30s', rest: '45s' },
              ],
            },
          ],
        },
      ],
    },
  },
]

export const findAthlete = (id: string) => SAMPLE_ATHLETES.find((a) => a.id === id)
